import { CoralException, ICoralArgumentType } from '@ch-types/CoralException';
import {
    AccessDeniedException,
    ClientException,
    DependencyFailure,
    IhmException,
    InsufficientQuantityException,
    InternalFailure,
    InternalServiceError,
    NotFoundException,
    ResourceVersionConflictException,
    RetryableException,
    ServiceException,
    ServiceUnavailableException,
    ValidationException,
} from '@ch-types/CoralExceptionTypes';
import { Exception } from '@ch-types/Exception';

type CoralExceptionMapType = Record<string, (args: ICoralArgumentType) => CoralException>;

export class CoralExceptionFactory {
    public static buildBaseCoralException(args: ICoralArgumentType): CoralException {
        return new CoralException(args);
    }

    /**
     *  Used to create an instance of the correct subclass of CoralException for the provided String type.
     *  If the provided "type" String is not registered then it will return a generic CoralException instance.
     *
     *  @param type - The String type that is returned in the "type" field of a Coral Exception Response
     *  @param serviceExceptionObj - The exception object returned by a coral service
     */
    public static buildCoralException(type: string, serviceExceptionObj: Error): CoralException {
        const args: ICoralArgumentType = {
            message: serviceExceptionObj.message,
            type,
            ...(serviceExceptionObj instanceof Exception && { cause: serviceExceptionObj.getCause() }),
            extraData: Object.fromEntries(Object.entries(serviceExceptionObj).filter(([key]) => !['name', 'message', 'type', 'cause'].includes(key))),
        };
        if (Object.prototype.hasOwnProperty.call(this.coralExceptionMap, type)) {
            return this.coralExceptionMap[type](args);
        } else {
            return new CoralException(args);
        }
    }

    public static buildAccessDeniedException(args: ICoralArgumentType): CoralException {
        return new AccessDeniedException(args);
    }
    public static buildIhmException(args: ICoralArgumentType): CoralException {
        return new IhmException(args);
    }
    public static buildNotFoundException(args: ICoralArgumentType): CoralException {
        return new NotFoundException(args);
    }
    public static buildClientException(args: ICoralArgumentType): CoralException {
        return new ClientException(args);
    }
    public static buildServiceException(args: ICoralArgumentType): CoralException {
        return new ServiceException(args);
    }
    public static buildInternalServiceError(args: ICoralArgumentType): CoralException {
        return new InternalServiceError(args);
    }
    public static buildDependencyFailure(args: ICoralArgumentType): CoralException {
        return new DependencyFailure(args);
    }
    public static buildValidationException(args: ICoralArgumentType): CoralException {
        return new ValidationException(args);
    }
    public static buildServiceUnavailableException(args: ICoralArgumentType): CoralException {
        return new ServiceUnavailableException(args);
    }
    public static buildInternalFailure(args: ICoralArgumentType): CoralException {
        return new InternalFailure(args);
    }
    public static buildResourceVersionConflictException(args: ICoralArgumentType): CoralException {
        return new ResourceVersionConflictException(args);
    }
    public static buildInsufficientQuantityException(args: ICoralArgumentType): CoralException {
        return new InsufficientQuantityException(args);
    }
    public static buildRetryableException(args: ICoralArgumentType): CoralException {
        return new RetryableException(args);
    }

    /**
     *  Used to register new types of CoralExceptions in the map. It is recommended that you register the
     *  exception type inside the constructor of the client that will actually throw that type of exception
     *  to make sure it is registered before the code that throws that exception has a chance to get called.
     *
     *  @param typeString - The String value that is returned in the "type" field of a Coral Exception Response
     *  @param objectReference - The class to map this type of exception to
     */
    public static registerNewType(typeString: string, objectReference: (args) => CoralException): void {
        if (Object.prototype.hasOwnProperty.call(this.coralExceptionMap, typeString)) {
            throw new Error('Cannot register Exception Type because it is already registered.');
        }

        this.coralExceptionMap[typeString] = objectReference;
    }

    private static coralExceptionMap: CoralExceptionMapType = {
        'com.amazon.coral.security#AccessDeniedException': (args) => new AccessDeniedException(args),
        'com.amazon.coral.service#AccessDeniedException': (args) => new AccessDeniedException(args),
        'com.amazon.ihm.coral.exceptions#IhmException': (args) => new IhmException(args),
        'com.amazon.ihm.coral.exceptions#NotFoundException': (args) => new NotFoundException(args),
        'com.amazon.ihm.coral.exceptions#ClientException': (args) => new ClientException(args),
        'com.amazon.ihm.coral.exceptions#ServiceException': (args) => new ServiceException(args),
        'com.amazon.ihm.coral.exceptions#InternalServiceError': (args) => new InternalServiceError(args),
        'com.amazon.ihm.coral.exceptions#DependencyFailure': (args) => new DependencyFailure(args),
        'com.amazon.coral.validate#ValidationException': (args) => new ValidationException(args),
        'com.amazon.coral.service#ServiceUnavailableException': (args) => new ServiceUnavailableException(args),
        'com.amazon.coral.service#InternalFailure': (args) => new InternalFailure(args),
        'com.amazon.ihm.coral.exceptions#ResourceVersionConflictException': (args) => new ResourceVersionConflictException(args),

        // Generic Rover exceptions
        'com.amazon.ihm.rover.coral.exceptions#InsufficientQuantityException': (args) => new InsufficientQuantityException(args),
        'com.amazon.ihm.rover.coral.exceptions#RetryableException': (args) => new RetryableException(args),
    };
}
